import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import Button from 'components/ui/button';
import Form from 'components/ui/form';
import Loader from 'components/ui/loader';
import ModalTitle from 'components/modal/components/ModalTitle';
import ModalContent from 'components/modal/components/ModalContent';
import ModalFooter from 'components/modal/components/ModalFooter';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import getCustomSelectItems from 'creators/getCustomSelectItems';
import { SettlementFormFields } from './SettlementFormFields';
import { useSettlementForm } from './useSettlementForm';
import { RootState, StoreProps } from 'store';
import './style.scss';
import { SelectInterface, SettlementData } from 'pages/settlement/types';
import I18nHelpers from 'i18n/I18nHelpers';

interface OwnProps {
  content: SettlementData;
}

interface ConnectedProps {
  statuses: SelectInterface;
  currencies: SelectInterface;
  userMerchant: SelectInterface;
  settlementBalanceUser: SelectInterface;
  twoFactorAuth: boolean;
}

type Props = OwnProps & StoreProps & IntlProps & ConnectedProps;

const WarningMessage = ({ getTranslate }: IntlProps) => (
  <div className="settlement-warning">
    {I18nHelpers.getJsxFromHtml(getTranslate('settlements.modal.warning.message'))}
  </div>
);

const UpdateSettlementData = (props: Props) => {
  const {
    content,
    getTranslate,
    currencies,
    statuses,
    dispatch,
    twoFactorAuth,
    userMerchant,
    settlementBalanceUser,
  } = props;
  const isNew = !content?.id;

  const { isLoading, validationErrors, formData, handlers, saveSection } = useSettlementForm({
    content,
    isNew,
    currencies,
    statuses,
    dispatch,
    getTranslate,
    twoFactorAuth,
    userMerchant,
    settlementBalanceUser
  });

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    await saveSection();
  }, [saveSection]);

  return (
    <div className="update-settlement-data">
      {isLoading && <Loader />}
      <ModalTitle>
        {getTranslate(isNew ? 'settlements.modal.addTitle' : 'settlements.modal.editTitle')}
      </ModalTitle>

      <ModalContent>
        <Form onSubmit={handleSubmit}>
          {isNew && <WarningMessage getTranslate={getTranslate} />}
          <SettlementFormFields
            isNew={isNew}
            isLoading={isLoading}
            content={content}
            formData={formData}
            handlers={handlers}
            validationErrors={validationErrors}
            options={{ currencies, statuses, userMerchant, settlementBalanceUser }}
            getTranslate={getTranslate}
          />
        </Form>
      </ModalContent>

      <ModalFooter>
        <Button
          text={getTranslate(
            isNew
              ? 'settlements.modal.buttons.addSettlement'
              : 'settlements.modal.buttons.updateSettlement',
          )}
          status="primary"
          onClick={saveSection}
        />
      </ModalFooter>
    </div>
  );
};

const mapStateToProps = (state: RootState): ConnectedProps => {
  const {
    filtersValues: {
      currency,
      settlementStatus,
      userMerchant,
      settlementBalanceUser,
    },
    user: {
      twoFactorAuth
    }
  } = state;

  return {
    twoFactorAuth: Boolean(twoFactorAuth),
    currencies: {
      isLoading: currency.isLoading,
      list: getCustomSelectItems({ list: currency?.list }),
    },
    statuses: {
      isLoading: settlementStatus.isLoading,
      list: getCustomSelectItems({ list: settlementStatus?.list }),
    },
    userMerchant: {
      isLoading: userMerchant.isLoading,
      list: getCustomSelectItems({ list: userMerchant?.list }),
    },
    settlementBalanceUser: {
      isLoading: settlementBalanceUser.isLoading,
      list: getCustomSelectItems({ list: settlementBalanceUser?.list }),
    },
  };
};

export default connect(mapStateToProps)(addTranslation(UpdateSettlementData));